import * as React from "react";
import { HeroSocialProof } from "./HeroSocialProof";
import { HeroActions } from "./HeroActions";

interface HeroHeadlineProps {
  title: React.ReactNode;
  subtitle: string;
  primaryAction?: { label: string; onClick?: () => void };
  secondaryAction?: { label: string; href?: string; onClick?: () => void };
  disclaimer?: React.ReactNode;
  socialProof?: { avatars: string[] };
  align?: "center" | "left";
  /** Hide the CTA buttons (used when they are rendered elsewhere, e.g. below hero media) */
  hideActions?: boolean;
}

export function HeroHeadline({
  title,
  subtitle,
  primaryAction,
  secondaryAction,
  disclaimer,
  socialProof,
  align = "center",
  hideActions = false,
}: HeroHeadlineProps) {
  const isLeft = align === "left";
  const showActions = !hideActions && (primaryAction || secondaryAction);
  const hasActions = showActions || disclaimer;

  return (
    <div
      className={
        isLeft
          ? "hero-headline-layout relative z-10 flex w-full flex-col px-6 pb-6 text-left lg:px-0 lg:text-left"
          : "hero-headline-layout relative z-10 mx-auto flex w-full max-w-4xl flex-col px-6 pb-6 text-center 3xl:-translate-y-10"
      }
    >
      {socialProof && (
        <HeroSocialProof align={isLeft ? "left" : "center"} />
      )}

      <h1 className="text-balance text-5xl leading-[1.05] tracking-tighter sm:text-6xl lg:text-6xl font-bold">
        {title}
      </h1>

      <p
        className={
          isLeft
            ? "mt-6 max-w-2xl text-pretty text-base text-muted-foreground sm:text-lg lg:mx-0 mr-auto"
            : "mx-auto mt-6 max-w-3xl text-pretty text-base text-muted-foreground sm:text-lg"
        }
      >
        {subtitle}
      </p>

      {hasActions && (
        <div className={isLeft ? "flex flex-col items-start lg:items-start" : "flex flex-col items-center"}>
          {disclaimer && (
            <div className="mt-6 text-xs text-muted-foreground">
              {disclaimer}
            </div>
          )}

          {showActions && (
            <HeroActions
              primaryAction={primaryAction}
              secondaryAction={secondaryAction}
              className={isLeft ? "mt-6 lg:justify-start" : "mt-6"}
            />
          )}
        </div>
      )}
    </div>
  );
}
